import { computed, reactive, ref, watch } from "vue";
import moment from "moment";
import { t } from "@/i18n";
import { makeLogger } from "@/shared/consola";
import { useGetters } from "@/store/vuex-composition-mappers";
import { mapThemeColorsAsCssVars } from "@/shared/themes/theme-colors-to-css-vars";

const logger = makeLogger(useOnboarding.name);

const FIVE_SECONDS = 5;
const AUTO_OPEN_RUlES = async (projectState) => {
    const { createdAt, updatedAt } = projectState;
    const diff = moment(updatedAt).diff(moment(createdAt), "seconds");
    logger.debug(
        "using default auto-open rule, up to 5seconds between create and update dates",
        {
            createdAt,
            updatedAt,
        },
        `diff is: ${diff < FIVE_SECONDS}`
    );
    return diff < FIVE_SECONDS;
};

const EMPTY_LOGOS = {
    link: "",
    squareLink: "",
};
const INITIAL_VALUES = {
    template: "",
    layout: "",
    mode: "by-product",
    hidePrompt: false,
    showProductPickerOnly: false,
    productSelectLater: true,
    logo: EMPTY_LOGOS,
};

/**
 * This is an ABSTRACTion over use{tool}CreatorStore state!
 * The form is needed to be an interface of EACH Pinia's - CreatorStore({tool}-creator) for the Onboarding process.
 *
 * That is why this API should be AVOIDED to use without CreatorStore!
 * That is not what is intended [!!!]
 *
 * When you need some data to onboard or {tool} creation process - you should use corresponding use{tool}CreatorStore.
 *
 * */
export function useOnboarding({
    autoOpenRules = AUTO_OPEN_RUlES,
    initialValues = INITIAL_VALUES,
} = {}) {
    const { defaultTheme } = useGetters("theme", ["defaultTheme"]);

    const onboardState = reactive({
        theme: defaultTheme.value,
        template: initialValues.template,
        layout: initialValues.layout,
        mode: initialValues.mode || INITIAL_VALUES.mode,
        hidePrompt: initialValues.hidePrompt,
        promptDescription: "",
        productDetails: {},
        productSelectLater: initialValues.productSelectLater || INITIAL_VALUES.productSelectLater,
        showProductPickerOnly: initialValues.showProductPickerOnly,
        error: "",
        isLoading: false,
        logo: { ...INITIAL_VALUES.logo },
        tenantData: {},
    });

    const isCreatorShown = ref(false);
    const previewCssVars = computed(() => mapThemeColorsAsCssVars(onboardState.theme));

    function toggleCreatorShown(value) {
        isCreatorShown.value = value === undefined ? !isCreatorShown.value : value;
    }

    async function autoOpenCreator(projectState) {
        const result = await autoOpenRules(projectState);
        if (result) {
            toggleCreatorShown(true);
        }
    }

    function updateProductInfo({ mode, promptDescription, productDetails }) {
        onboardState.mode = mode;
        onboardState.promptDescription = promptDescription;
        onboardState.productDetails = productDetails;
    }

    function validate() {
        onboardState.error = "";
        if (
            onboardState.mode === "by-product" &&
            !onboardState.productSelectLater &&
            !onboardState.productDetails?.id
        ) {
            onboardState.error = t("shared.creator.error_no_product_selected");
        } else if (
            onboardState.mode === "by-prompt" &&
            (!onboardState.promptDescription || onboardState.promptDescription.length < 15) &&
            !onboardState.hidePrompt
        ) {
            onboardState.error = t("shared.creator.error_no_prompt_provided");
        }
        return !onboardState.error;
    }

    // @business: initial theme value should be default (but it can happen in time)
    watch(
        defaultTheme,
        () => {
            onboardState.theme = defaultTheme.value;
            const { logo, logoSquare } = defaultTheme.value || {};
            onboardState.logo.link = logo || "";
            onboardState.logo.squareLink = logoSquare || "";
        },
        { immediate: true }
    );

    return {
        onboardState,
        isCreatorShown,
        previewCssVars,
        updateProductInfo,
        toggleCreatorShown,
        autoOpenCreator,
        validate,
    };
}
